import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PublicNavbar from '../components/public/PublicNavbar';
import PublicFooter from '../components/public/PublicFooter';
const LAST_UPDATED_LABEL = 'March 17, 2026';

const CookiePolicyPage = () => {
  const { t } = useTranslation('legal');
  useEffect(() => {
    const previousTitle = document.title;
    const previousDescription = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';

    document.title = 'Cookie Policy | Hairchitech';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Read Hairchitech cookie policy: cookie categories, consent controls, third-party services, and data handling.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (metaDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-transparent relative text-white overflow-hidden">
      <PublicNavbar />

      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800" />
      <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.1) 0%, rgba(125, 102, 51, 0.05) 50%, transparent 100%)' }} />
      <div
        className="absolute top-0 right-0 h-[150%] w-1 z-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom left, rgba(201,160,56,0.3) 0%, transparent 80%)',
          transform: 'translate(50%, -50%) rotate(45deg)',
          transformOrigin: 'top right'
        }}
      />
      <div className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.1) 0%, transparent 70%)' }} />
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.15) 0%, transparent 70%)' }} />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl" />
      <div className="absolute top-40 left-20 w-64 h-1 rounded-full transform rotate-45" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.3) 0%, transparent 100%)' }} />
      <div className="absolute bottom-40 right-20 w-48 h-1 rounded-full transform -rotate-12" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.2) 0%, transparent 100%)' }} />

      <header className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-14">
          <span
            className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border"
            style={{ borderColor: 'rgba(0, 151, 178, 0.35)', color: '#9ee7f2', background: 'rgba(0, 151, 178, 0.12)' }}
          >
            {t('legal.last_updated')} {LAST_UPDATED_LABEL}
          </span>
          <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
            {t('legal.cookie_title')}
          </h1>
          <p className="mt-4 text-lg md:text-xl text-white/75 max-w-3xl">
{t('legal.cookie_subtitle')}
          </p>
        </div>
      </header>

      <main className="relative z-10 pb-24 px-6 lg:px-8">
        <article className="max-w-3xl mx-auto rounded-2xl border border-white/20 bg-white/5 backdrop-blur-xl shadow-2xl p-6 sm:p-8 md:p-10 space-y-10">
          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.cookie_s1_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.cookie_s1_body')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.cookie_s2_title')}</h2>
            <div className="mt-4 space-y-5 text-white/80">
              <div>
                <h3 className="text-lg font-semibold text-white">{t('legal.cookie_s2_essential_title')}</h3>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>{t('legal.cookie_s2_essential_item1')}</li>
                  <li>{t('legal.cookie_s2_essential_item2')}</li>
                  <li>{t('legal.cookie_s2_essential_item3')}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t('legal.cookie_s2_analytics_title')}</h3>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>{t('legal.cookie_s2_analytics_item1')}</li>
                  <li>{t('legal.cookie_s2_analytics_item2')}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t('legal.cookie_s2_marketing_title')}</h3>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>{t('legal.cookie_s2_marketing_item1')}</li>
                  <li>{t('legal.cookie_s2_marketing_item2')}</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.cookie_s3_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.cookie_s3_body')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.cookie_s4_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.cookie_s4_body')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.cookie_s5_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.cookie_s5_body')}
            </p>
            <p className="mt-3 font-medium text-white">
{t('legal.cookie_s5_note')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.cookie_s6_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
{t('legal.cookie_s6_body')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold" style={{ color: '#0097b2' }}>{t('legal.cookie_s7_title')}</h2>
            <p className="mt-3 text-white/80 leading-7">
              {t('legal.cookie_s7_prefix')}
              {' '}
              <a href="mailto:privacy@hairchitech.com" className="underline hover:no-underline" style={{ color: '#9ee7f2' }}>
                privacy@hairchitech.com
              </a>
              {' '}
              {' '}{t('legal.cookie_s7_suffix')}
              {' '}
              <a href="https://hairchitech.com" className="underline hover:no-underline" style={{ color: '#9ee7f2' }}>
                hairchitech.com
              </a>
              .
            </p>
          </section>
        </article>
      </main>
      <div className="relative z-10">
        <PublicFooter />
      </div>
    </div>
  );
};

export default CookiePolicyPage;
